<template>
  <v-container fluid class="pa-0 mt-n4">
    <DataTable
      :loading="loading"
      :items="tractSummaries"
      :headers="tractSummaryHeaders"
      :customCells="customCells"
      :enableExternalSearch="true"
      :showSearch="false"
      :externalSearch="search"
      @filtered-items="filteredTractsChanged"
      resize
    >
    <template #average-net-tons="{item}">
      <span>
        {{ item?.loads ? tonStringFromPounds(item.netWeight / item.loads) : '-' }}
      </span>
    </template>
    <template #production-cost-per-ton="{item}">
      <span>
        {{ item.productionCost ? formatMoney(item.productionCost / (item.netWeight / 2000)) : '' }}
      </span>
    </template>
    <template #stumpage-cost-per-ton="{item}">
      <span>
        {{ item.stumpageCost ? formatMoney(item.stumpageCost / (item.netWeight / 2000)) : '' }}
      </span>
    </template>
    <template #logging-cost-per-ton="{item}">
      <span>
        {{formatMoney(item.loggingCost / (item.netWeight / 2000))}}
      </span>
    </template>
    <template #hauling-cost-per-ton="{item}">
      <span>
        {{formatMoney(item.haulingCost / (item.netWeight / 2000))}}
      </span>
    </template>
    <template #rpi="{item}">
      <span> {{parseFloat(item.rpi) > 0 ? item.rpi.toFixed(2) : ''}} </span>
    </template>
    <template #dbh="{item}">
      <span> {{parseInt(item.dbh) > 0 ? item.dbh : ''}} </span>
    </template>
    <template #age="{item}">
      <span> {{parseInt(item.age) > 0 ? item.age : ''}} </span>
    </template>
    <template #plf="{item}">
      <span> {{parseFloat(item.plf) > 0 ? item.plf : ''}} </span>
    </template>
    <template #averageSED="{item}">
      <span> {{parseFloat(item.averageSED) > 0 ? item.averageSED : ''}} </span>
    </template>
    <template #actions="{item}">
      <Icon
      icon="mdi-information-outline"
      :dataTestId="`product-${item.name}`"
      :small="false"
      :tooltipText="$t('viewDetailedBreakdown')"
      @icon-clicked="openDetailDialog(item)"/>
    </template>
      <template #body.append>
        <tr class="font-weight-bold">
          <td :class="col.class" v-for="col in summariesByTractCols" :key="col.id">
            <v-tooltip bottom color="secondary" v-if="col.tooltip">
              <template #activator="{ on }">
                <span v-on="on">{{ col.value }}</span>
              </template>
              <span class="subtitle-1 white--text">{{ col.tooltip }}</span>
            </v-tooltip>
            <span v-else>{{ col.value }}</span>
          </td>
        </tr>
      </template>
    </DataTable>
    <Dialog :stateId="dialogId" @dialog-closing="resetDialogs">
      <TractCostDetail
        v-if="focusedTractSummary && !speciesMode"
        :dateRange="dateRange"
        :entity="entity"
        :tractSummary="focusedTractSummary"
        :tractTypeCategory="tractTypeCategory"
      />
      <TractSpeciesCostDetail
        v-if="focusedTractSummary && speciesMode"
        :dateRange="dateRange"
        :entity="entity"
        :tractSummary="focusedTractSummary"
        :tractTypeCategory="tractTypeCategory"
      />
    </Dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { formatMoney, tonStringFromPounds } from '@/utils/NumericMutations.js'
import { getDateRangeForPeriod } from '@/utils/DateFormatter.js'
import { TractTypeCategory } from '@/utils/Enumerations.js'
import { getAggregatedValueForHeader, getAggregateTooltip } from '@/headers/HeaderHelpers'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'ProductSummaryByTract',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    TractCostDetail: () => import('@/components/tract/tract-analysis/TractCostDetail.vue'),
    TractSpeciesCostDetail: () => import('@/components/tract/tract-analysis/TractSpeciesCostDetail.vue')
  },

  props: {
    tractSummaries: {
      type: Array,
      required: true
    },
    entity: {
      type: Object,
      required: false,
      default: undefined
    },
    dateRange: {
      type: Object,
      required: false,
      default: undefined
    },
    search: {
      type: String,
      default: ''
    },
    columnSet: {
      type: Object,
      required: true
    },
    chosenCategory: {
      type: Number,
      required: true
    },
    speciesMode: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    dialogId: 'product-summary-by-tract',
    selectedEntity: undefined,
    tractCategories: TractTypeCategory.names,
    focusedTractSummary: null,
    filteredTractSummaries: undefined
  }),

  computed: {
    ...mapGetters('user', ['businessEntities']),

    loading () {
      return !this.entity || !this.dateRange || !this.tractSummaries
    },

    tractTypeCategory () {
      return TractTypeCategory.forInt(this.chosenCategory)
    },

    customCells () {
      return [
        {
          slotName: 'average-net-tons',
          value: 'averageWeight'
        },
        {
          slotName: 'production-cost-per-ton',
          value: 'productionCostPerTon'
        },
        {
          slotName: 'stumpage-cost-per-ton',
          value: 'stumpageCostPerTon'
        },
        {
          slotName: 'rpi',
          value: 'rpi'
        },
        {
          slotName: 'dbh',
          value: 'dbh'
        },
        {
          slotName: 'age',
          value: 'age'
        },
        {
          slotName: 'plf',
          value: 'plf'
        },
        {
          slotName: 'averageSED',
          value: 'averageSED'
        },
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'logging-cost-per-ton',
          value: 'loggingCostPerTon'
        },
        {
          slotName: 'hauling-cost-per-ton',
          value: 'haulingCostPerTon'
        }
      ]
    },

    tractSummaryHeaders () {
      if (!this.columnSet) return []
      return this.columnSet.getColumns()
    },

    summariesByTractCols () {
      const moreThanOneTract = this.tractSummaries.length > 1 && (!this.filteredTractSummaries || this.filteredTractSummaries.length > 1)
      if (moreThanOneTract) {
        return this.tractSummaryHeaders.map(this.tractHeaderToSummaryMapper)
      } else {
        return []
      }
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    formatMoney,
    tonStringFromPounds,
    getDateRangeForPeriod,

    openDetailDialog (tract) {
      this.focusedTractSummary = tract
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.focusedTractSummary = null
    },

    tractHeaderToSummaryMapper (header) {
      const result = {
        id: header.value,
        class: `text-${header.align ?? 'left'}`,
        tooltip: getAggregateTooltip(header.aggregateType, header.weightedBy),
        value: '-'
      }

      if (header.value === 'name') {
        result.value = this.$t('totals')
        return result
      }

      this.getAggregateValues(header, result)
      return result
    },

    getAggregateValues (header, result) {
      if (!header.aggregateType) {
        result.value = '-'
        return
      }
      const tractSummaries = this.getTractSummariesMapped()
      let value = 0
      value = getAggregatedValueForHeader(tractSummaries, header)
      if (value === 0) {
        result.value = ''
      } else {
        result.value = header.format && header.aggregateType ? header.format(value) : value
      }
    },

    getTractSummariesMapped () {
      let tractSummaries = this.tractSummaries
      if (this.filteredTractSummaries) tractSummaries = this.filteredTractSummaries

      tractSummaries = cloneDeep(tractSummaries)
      tractSummaries = tractSummaries.map(ts => ({
        ...ts,
        averageWeight: (ts.netWeight) / ts.loads,
        productionCostPerTon: ts.productionCost / (ts.netWeight / 2000),
        stumpageCostPerTon: ts.stumpageCost / (ts.netWeight / 2000),
        loggingCostPerTon: ts.loggingCost / (ts.netWeight / 2000),
        haulingCostPerTon: ts.haulingCost / (ts.netWeight / 2000)
      }))
      return tractSummaries
    },

    filteredTractsChanged (filteredTracts) {
      this.filteredTractSummaries = filteredTracts
    }
  }
}
</script>
